import React, { useState, useEffect, useRef, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Ticket,
  Users,
  DollarSign,
  Settings,
  LogOut,
  UserCog,
  Plus,
  Trash2,
  Check,
  Clock,
  ChevronDown,
  Search,
  X,
  QrCode,
  TrendingUp,
  Camera,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Edit,
  Download,
  FileSpreadsheet,
  FileText,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tag,
  FormInput,
  Calendar,
  Mail,
  Send,
  Eye,
  Menu,
  Paperclip,
  Wrench,
  Lock,
  CreditCard,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { useAuth } from "../../context/AuthContext";
import { canAccess } from "../../lib/permissions";
import { Html5Qrcode } from "html5-qrcode";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { SessionManagement, CouponManagement, CustomFieldsManagement } from "../../components/AdminSettings";
import RichTextEditor from "../../components/RichTextEditor";
import {
  SettingsPasswordDialog,
  isSettingsUnlocked,
} from "../../components/admin/SettingsPasswordGate";

import { API_URL } from "../../config";

export const Sidebar = ({ mobileOpen, setMobileOpen }) => {
  const { logout, user } = useAuth();
  const role = user?.role || "master";
  const navigate = useNavigate();
  const [pwdOpen, setPwdOpen] = useState(false);

  const handleSettingsClick = (e) => {
    if (isSettingsUnlocked()) return; // already unlocked, allow nav
    e.preventDefault();
    setMobileOpen(false);
    setPwdOpen(true);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const navItems = [
    { path: "/admin",            id: "dashboard",     icon: LayoutDashboard, label: "Dashboard", exact: true },
    { path: "/admin/sales",      id: "tickets",       icon: Ticket,          label: "Bilhetes" },
    { path: "/admin/plans",      id: "plans",         icon: CreditCard,      label: "Planos" },
    { path: "/admin/buyers",     id: "buyers",        icon: Users,           label: "Compradores" },
    { path: "/admin/emails",     id: "communication", icon: Mail,            label: "Comunicação" },
    { path: "/admin/scanner",    id: "checkins",      icon: QrCode,          label: "Check-Ins" },
    { path: "/admin/financial",  id: "finance",       icon: "euro",          label: "Finanças" },
    { path: "/admin/users",      id: "users",         icon: Users,           label: "Utilizadores" },
    { path: "/admin/admins",     id: "admins",        icon: Users,           label: "Admins" },
    { path: "/admin/settings",   id: "settings",      icon: Settings,        label: "Configurações" },
  ].filter((it) => canAccess(role, it.id));

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <aside
        className={`sidebar w-64 h-screen fixed left-0 top-0 z-50 transition-transform duration-200 flex flex-col ${
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="admin-sidebar"
      >
        <div className="p-6 pb-4 flex items-center justify-between shrink-0">
          <div>
            <h1 className="text-xl font-bold text-white">Bilhetez</h1>
            <p className="text-zinc-500 text-sm">Gestão de Eventos</p>
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden text-zinc-400 hover:text-white p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="space-y-2 flex-1 overflow-y-auto px-6">
          {navItems.map((item) => {
            const isSettings = item.path === "/admin/settings";
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.exact}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? "active" : ""}`
                }
                onClick={(e) => {
                  if (isSettings) {
                    handleSettingsClick(e);
                    if (e.defaultPrevented) return;
                  }
                  setMobileOpen(false);
                }}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                {item.icon === "euro" ? (
                  <span className="w-5 h-5 flex items-center justify-center text-base font-bold">€</span>
                ) : (
                  <item.icon className="w-5 h-5" />
                )}
                <span>{item.label}</span>
                {isSettings && !isSettingsUnlocked() && (
                  <Lock className="w-3.5 h-3.5 ml-auto text-zinc-500" />
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-6 pt-4 space-y-2 shrink-0 border-t border-white/5">
          {user && (
            <div className="px-3 py-2 rounded-lg bg-white/5 border border-white/10">
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Sessão ativa</p>
              <p className="text-white text-sm font-medium truncate" data-testid="sidebar-current-admin">{user.email}</p>
              <p className="text-[10px] text-amber-400 uppercase tracking-wider mt-0.5">{user.role}</p>
            </div>
          )}
          <button
            onClick={async () => { await logout(); navigate("/admin/login"); }}
            className="sidebar-link w-full text-violet-300 hover:text-violet-200 hover:bg-violet-500/10"
            data-testid="switch-admin-btn"
            title="Termina a sessão e vai para o ecrã de login"
          >
            <UserCog className="w-5 h-5" />
            <span>Mudar de Admin</span>
          </button>
          <button
            onClick={handleLogout}
            className="sidebar-link w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      <SettingsPasswordDialog
        open={pwdOpen}
        onOpenChange={setPwdOpen}
        onSuccess={() => navigate("/admin/settings")}
        currentEmail={user?.email}
      />
    </>
  );
};

// Export utilities moved to /app/frontend/src/lib/export.js
// Sortable Table Header Component
